// Utility to parse content and extract field patterns dynamically

/**
 * Analyzes content sections to identify common field patterns
 * @param {Array} sections - Array of parsed sections with content
 * @returns {Array} Array of field definitions
 */
export const analyzeContentFields = (sections) => {
  if (!sections || sections.length === 0) return getDefaultFields();

  const fieldPatterns = new Map();
  const fieldValues = new Map();

  sections.forEach(section => {
    if (!section.content) return;

    // Extract fields that follow patterns like "**Field Name:**" or "Field Name:"
    const fieldMatches = section.content.matchAll(/\*?\*?([A-Za-z\s]+):\*?\*?\s*([^\n]*)/g);
    
    for (const match of fieldMatches) {
      const fieldName = match[1].trim();
      const fieldValue = match[2].trim();
      
      // Skip if it's part of the title (e.g., "F001: Feature Title")
      if (section.title && section.title.includes(fieldName)) continue;
      
      // Track field occurrences
      fieldPatterns.set(fieldName, (fieldPatterns.get(fieldName) || 0) + 1);
      
      // Collect sample values to determine field type
      if (!fieldValues.has(fieldName)) {
        fieldValues.set(fieldName, new Set());
      }
      if (fieldValue) {
        fieldValues.get(fieldName).add(fieldValue.toLowerCase());
      }
    }

    // Also check for sections with list patterns (like Acceptance Criteria)
    const listPatterns = section.content.matchAll(/\*?\*?([A-Za-z\s]+):\*?\*?\s*\n((?:[-•*]\s*.+\n?)+)/g);
    for (const match of listPatterns) {
      const fieldName = match[1].trim();
      fieldPatterns.set(fieldName, (fieldPatterns.get(fieldName) || 0) + 1);
      if (!fieldValues.has(fieldName)) {
        fieldValues.set(fieldName, new Set(['[list]']));
      }
    }
  });

  // Convert patterns to field definitions
  const fields = [];
  const minOccurrences = Math.max(1, Math.floor(sections.length * 0.3)); // Field must appear in at least 30% of sections

  fieldPatterns.forEach((count, fieldName) => {
    if (count >= minOccurrences) {
      const field = createFieldDefinition(fieldName, fieldValues.get(fieldName) || new Set());
      if (field) fields.push(field);
    }
  });

  // Always include title and description as base fields
  const hasTitle = fields.some(f => f.name === 'title');
  const hasDescription = fields.some(f => f.name === 'description');
  
  if (!hasTitle) {
    fields.unshift({
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter title'
    });
  }
  
  if (!hasDescription) {
    fields.splice(1, 0, {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: true,
      rows: 3,
      placeholder: 'Enter detailed description'
    });
  }

  return fields.length > 2 ? fields : getDefaultFields();
};

/**
 * Creates a field definition based on field name and sample values
 */
const createFieldDefinition = (fieldName, sampleValues) => {
  const normalizedName = fieldName.toLowerCase().replace(/\s+/g, '');
  const camelCaseName = normalizedName.replace(/\s+(.)/g, (_, char) => char.toUpperCase());

  // Determine field type based on name and values
  let fieldType = 'text';
  let options = null;
  let rows = 2;

  // Check if it's a list field
  if (sampleValues.has('[list]')) {
    fieldType = 'textarea';
    rows = 3;
  }
  // Check if it's a select field based on limited set of values
  else if (sampleValues.size > 0 && sampleValues.size <= 5) {
    const values = Array.from(sampleValues);
    const commonValues = ['high', 'medium', 'low', 'critical', 'major', 'minor', 'yes', 'no', 'true', 'false'];
    const hasCommonValues = values.some(v => commonValues.includes(v));
    
    if (hasCommonValues || sampleValues.size <= 3) {
      fieldType = 'select';
      options = values.map(v => ({
        value: v,
        label: v.charAt(0).toUpperCase() + v.slice(1)
      }));
      
      // Ensure standard options for common fields
      if (normalizedName.includes('priority')) {
        // Use MoSCoW prioritization for priority fields
        options = [
          { value: 'must-have', label: 'Must have' },
          { value: 'should-have', label: 'Should have' },
          { value: 'could-have', label: 'Could have' },
          { value: 'wont-have', label: "Won't have" }
        ];
      } else if (normalizedName.includes('complexity') || normalizedName.includes('severity')) {
        options = [
          { value: 'high', label: 'High' },
          { value: 'medium', label: 'Medium' },
          { value: 'low', label: 'Low' }
        ];
      }
    }
  }
  // Multi-line fields
  else if (normalizedName.includes('criteria') || normalizedName.includes('dependencies') || 
           normalizedName.includes('notes') || normalizedName.includes('details') ||
           normalizedName.includes('description') || normalizedName.includes('userimpact') ||
           normalizedName.includes('impact')) {
    fieldType = 'textarea';
    rows = 3;
  }

  // Provide helpful placeholder text based on field type
  let placeholder = `Enter ${fieldName.toLowerCase()}`;
  
  if (normalizedName.includes('criteria')) {
    placeholder = 'Enter acceptance criteria, one per line:\nThe user should be able to...\nThe system must...';
  } else if (normalizedName.includes('dependencies')) {
    placeholder = 'List any dependencies, one per line';
  } else if (normalizedName.includes('userimpact') || normalizedName.includes('impact')) {
    placeholder = 'Describe how this feature impacts users and their experience';
  }
  
  return {
    name: camelCaseName,
    label: fieldName,
    type: fieldType,
    required: false,
    ...(fieldType === 'textarea' && { rows }),
    ...(fieldType === 'select' && { options }),
    ...(fieldType === 'text' && { placeholder: `Enter ${fieldName.toLowerCase()}` }),
    ...(fieldType === 'textarea' && { placeholder })
  };
};

/**
 * Get default fields when no content is available to analyze
 */
const getDefaultFields = () => {
  return [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter title'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: true,
      rows: 4,
      placeholder: 'Enter detailed description'
    }
  ];
};

/**
 * Format field value for display
 */
export const formatFieldValue = (value, fieldType) => {
  if (!value) return '';
  
  if (fieldType === 'select') {
    // Handle MoSCoW values
    if (value.includes('-')) {
      return value.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  
  return value;
};

/**
 * Generate content from form data based on field definitions
 */
export const generateContentFromFields = (formData, fields) => {
  let content = '';
  const sections = [];
  
  // Start with description if it exists - with the Description label
  if (formData.description) {
    sections.push(`**Description:** ${formData.description}`);
  }
  
  // Group and order fields for consistent formatting
  const orderedFields = [];
  const fieldOrder = ['priority', 'complexity', 'userimpact', 'acceptancecriteria', 'dependencies'];
  
  // First add fields in the preferred order
  fieldOrder.forEach(fieldName => {
    const field = fields.find(f => f.name.toLowerCase().replace(/\s+/g, '') === fieldName);
    if (field && formData[field.name]) {
      orderedFields.push(field);
    }
  });
  
  // Then add any remaining fields
  fields.forEach(field => {
    if (field.name === 'title' || field.name === 'description') return;
    if (!orderedFields.includes(field) && formData[field.name]) {
      orderedFields.push(field);
    }
  });
  
  // Format each field appropriately
  orderedFields.forEach(field => {
    const value = formData[field.name];
    if (!value) return;

    // For textarea fields, check if value has multiple lines or bullet points — format as list
    if (field.type === 'textarea') {
      const lines = value.split('\n').filter(line => line.trim());
      const hasBullets = lines.some(line => line.trim().match(/^[-•*]\s/));
      const isMultiLine = lines.length > 1;

      if (hasBullets || isMultiLine) {
        const formattedList = lines.map(line => {
          if (!line.trim().match(/^[-•*]\s/)) {
            return `- ${line.trim()}`;
          }
          return line;
        }).join('\n');
        sections.push(`**${field.label}:**\n${formattedList}`);
      } else {
        sections.push(`**${field.label}:** ${value.trim()}`);
      }
    } else {
      // For text/select fields, use inline formatting
      sections.push(`**${field.label}:** ${formatFieldValue(value, field.type)}`);
    }
  });
  
  // Join all sections with proper spacing
  content = sections.join('\n\n');
  
  return content;
};